"use client";

import { useRef, useEffect, useState, useCallback } from "react";
import { getFieldErrors, ZodTreeError } from "@/types/forms";
import FieldError from "@/app/ui/atomic/content/FieldError";

function validateField(
  value: string,
  opts: { required?: boolean; minLength?: number; maxLength?: number; type?: string }
): string | null {
  if (opts.required && !value.trim()) return "This field is required.";
  if (value.trim() && opts.minLength && value.length < opts.minLength) {
    if (opts.maxLength && opts.minLength === opts.maxLength)
      return `Must be exactly ${opts.minLength} characters.`;
    return `Must be at least ${opts.minLength} characters.`;
  }
  if (value.trim() && opts.maxLength && value.length > opts.maxLength)
    return `Must be ${opts.maxLength} characters or less.`;
  if (value.trim() && opts.type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
    return "Please enter a valid email address.";
  return null;
}

interface FormProps {
  name: string;
  action: (data: FormData) => void | Promise<void>;
  children: React.ReactNode;
  error?: string;
  hasDraft?: boolean;
  lastSaved?: Date | null;
  onClearDraft?: () => void;
};

export function Form({ name, action, children, error, hasDraft, lastSaved, onClearDraft }: FormProps) {
  const errorRef = useRef<HTMLDivElement | null>(null);
  const prevErrorRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (error && error !== prevErrorRef.current && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      errorRef.current.focus({ preventScroll: true });
    }
    prevErrorRef.current = error;
  }, [error]);

  return (
    <form 
      action={action}
      className="w-full max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-6 sm:py-8 lg:py-12"
    >
      <div className="relative rounded-2xl shadow-xl overflow-hidden border border-white/10 bg-gradient-to-b from-(--purple)/95 to-(--dark-purple)">
        <div className="absolute inset-0 bg-gradient-to-br from-(--brand)/10 via-transparent to-(--gold)/5 pointer-events-none" />

        {hasDraft && lastSaved && onClearDraft && (
          <div className="relative px-4 pt-4 sm:px-8 lg:px-12">
            <div className="flex items-center justify-between text-sm text-white/70 p-3 bg-white/10 rounded-lg border border-white/15">
              <span>Draft saved at {lastSaved.toLocaleTimeString()}</span>
              <button
                type="button"
                onClick={onClearDraft}
                className="text-(--gold) hover:underline font-medium"
              >
                Clear form
              </button>
            </div>
          </div>
        )}
        <div className="relative px-4 py-6 sm:px-8 sm:py-8 lg:px-12 lg:py-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-7 lg:gap-8">
            {children}
          </div>
        </div>
        <div className="relative">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-(--brand)/40 to-transparent" />
          <div className="bg-(--dark-purple)/40 px-4 py-5 sm:px-8 sm:py-6 lg:px-12">
            {error && (
              <div
                ref={errorRef}
                tabIndex={-1}
                data-form-error="true"
                className="mb-4 bg-red-500/20 border border-red-500/30 rounded-lg px-4 py-3 outline-none"
              >
                <p className="text-red-200 text-center font-medium" role="alert">{error}</p>
              </div>
            )}
            <button 
              type="submit" 
              className="block w-full max-w-md mx-auto font-bold rounded-full py-4 px-8 text-lg lg:text-xl transition-all hover:scale-105 hover:shadow-lg text-(--dark-purple) bg-gradient-to-r from-(--brand) to-(--gold) shadow-md shadow-(--brand)/20 hover:opacity-90"
            >
              Submit {name}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}

interface InputProps {
  id: string;
  type: string;
  label: string;
  placeholder: string;
  maxLength: number;
  minLength?: number;
  required?: boolean;
  errors?: ZodTreeError | string[];
  defaultValue?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  inputMode?: "text" | "numeric" | "email" | "tel" | "search" | "url" | "none" | "decimal";
  pattern?: string;
  filterInput?: (value: string) => string;
  autoComplete?: string;
};

export function Input({
  id,
  type,
  label,
  placeholder,
  maxLength,
  minLength,
  required,
  errors,
  defaultValue,
  onChange,
  inputMode,
  pattern,
  filterInput,
  autoComplete,
}: InputProps) {
  const serverErrors = getFieldErrors(errors);
  const [blurError, setBlurError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const allErrors = serverErrors.length > 0 ? serverErrors : (touched && blurError ? [blurError] : []);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setTouched(true);
    const err = validateField(e.target.value, { required, minLength, maxLength, type });
    setBlurError(err);
  }, [required, minLength, maxLength, type]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (filterInput) {
      const filtered = filterInput(e.target.value);
      if (filtered !== e.target.value) {
        e.target.value = filtered;
      }
    }
    if (type === "number" && maxLength && e.target.value.length > maxLength) {
      e.target.value = e.target.value.slice(0, maxLength);
    }
    if (touched && blurError) {
      const err = validateField(e.target.value, { required, minLength, maxLength, type });
      setBlurError(err);
    }
    onChange?.(e);
  }, [onChange, touched, blurError, required, minLength, maxLength, type, filterInput]);

  useEffect(() => {
    if (serverErrors.length > 0) setTouched(false); // eslint-disable-line react-hooks/set-state-in-effect -- reset validation state when server errors arrive
  }, [serverErrors]);

  return (
    <div className="relative flex flex-col gap-2.5 pb-6">
      <label 
        htmlFor={id}
        className="text-sm lg:text-base text-(--gold) font-semibold min-h-[3.25rem] flex items-end leading-snug"
      >
        {label}
        {required && <span className="text-(--brand) ml-1">*</span>}
      </label>
      <input
        className={`w-full h-[50px] px-4 py-3 text-base bg-white/10 text-white border rounded-lg focus:ring-2 focus:ring-(--brand)/30 focus:outline-none transition-all placeholder:text-white/40 backdrop-blur-sm ${
          allErrors.length > 0
            ? "border-red-400 focus:border-red-400"
            : "border-white/20 focus:border-(--brand)"
        }`}
        type={type}
        id={id}
        name={id}
        placeholder={placeholder}
        required={required ? true : false}
        maxLength={type !== "number" ? maxLength : undefined}
        minLength={minLength ? minLength : undefined}
        defaultValue={defaultValue}
        onChange={handleChange}
        onBlur={handleBlur}
        inputMode={inputMode}
        pattern={pattern}
        autoComplete={autoComplete}
        aria-invalid={allErrors.length > 0}
        aria-describedby={allErrors.length > 0 ? allErrors.map((_, i) => `${id}-error-${i}`).join(" ") : undefined}
      />
      <div className="absolute bottom-0 left-0 right-0">
        <FieldError id={id} errors={allErrors} />
      </div>
    </div>
  );
};

interface SelectProps {
  id: string;
  label: string;
  defaultValue?: string;
  required?: boolean;
  placeholderOption?: string;
  options: ({
    value: string
    label: string
  })[];
  errors?: ZodTreeError | string[];
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
};

export function Select({
  id,
  label,
  defaultValue,
  required,
  placeholderOption,
  options,
  errors,
  onChange
}: SelectProps) {
  const serverErrors = getFieldErrors(errors);
  const [blurError, setBlurError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const allErrors = serverErrors.length > 0 ? serverErrors : (touched && blurError ? [blurError] : []);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLSelectElement>) => {
    setTouched(true);
    if (required && !e.target.value) {
      setBlurError("Please select an option.");
    } else {
      setBlurError(null);
    }
  }, [required]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    if (touched && blurError) {
      if (required && !e.target.value) {
        setBlurError("Please select an option.");
      } else {
        setBlurError(null);
      }
    }
    onChange?.(e);
  }, [onChange, touched, blurError, required]);

  useEffect(() => {
    if (serverErrors.length > 0) setTouched(false); // eslint-disable-line react-hooks/set-state-in-effect -- reset validation state when server errors arrive
  }, [serverErrors]);

  return (
    <div className="relative flex flex-col gap-2.5 pb-6">
      <label 
        htmlFor={id}
        className="text-sm lg:text-base text-(--gold) font-semibold min-h-[3.25rem] flex items-end leading-snug"
      >
        {label}
        {required && <span className="text-(--brand) ml-1">*</span>}
      </label>
      <select
        className={`w-full h-[50px] px-4 py-3 pr-10 text-base bg-white/10 text-white border rounded-lg focus:ring-2 focus:ring-(--brand)/30 focus:outline-none transition-all cursor-pointer appearance-none backdrop-blur-sm bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23FFCC01%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E')] bg-[length:0.65em] bg-[right_0.75rem_center] bg-no-repeat ${
          allErrors.length > 0
            ? "border-red-400 focus:border-red-400"
            : "border-white/20 focus:border-(--brand)"
        }`}
        id={id}
        name={id}
        defaultValue={defaultValue ?? ""}
        required={required ? true : false}
        onChange={handleChange}
        onBlur={handleBlur}
        aria-invalid={allErrors.length > 0}
        aria-describedby={allErrors.length > 0 ? allErrors.map((_, i) => `${id}-error-${i}`).join(" ") : undefined}
      >
        {placeholderOption && (
          <option value="" disabled style={{ backgroundColor: "#1D092C", color: "rgba(255,255,255,0.6)" }}>
            {placeholderOption}
          </option>
        )}
        {options.map(({ value, label }) => (
          <option key={value} value={value} style={{ backgroundColor: "#1D092C", color: "#ffffff" }}>
            {label}
          </option>
        ))}
      </select>
      <div className="absolute bottom-0 left-0 right-0">
        <FieldError id={id} errors={allErrors} />
      </div>
    </div>
  );
};

interface TextAreaProps {
  id: string;
  label: string;
  placeholder: string;
  maxLength: number;
  required?: boolean;
  errors?: ZodTreeError | string[];
  defaultValue?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
};

export function TextArea({
  id,
  label,
  placeholder,
  maxLength,
  required,
  errors,
  defaultValue,
  onChange
}: TextAreaProps) {
  const serverErrors = getFieldErrors(errors);
  const [blurError, setBlurError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const allErrors = serverErrors.length > 0 ? serverErrors : (touched && blurError ? [blurError] : []);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
    setTouched(true);
    const err = validateField(e.target.value, { required, maxLength });
    setBlurError(err);
  }, [required, maxLength]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (touched && blurError) {
      const err = validateField(e.target.value, { required, maxLength });
      setBlurError(err);
    }
    onChange?.(e);
  }, [onChange, touched, blurError, required, maxLength]);

  useEffect(() => {
    if (serverErrors.length > 0) setTouched(false); // eslint-disable-line react-hooks/set-state-in-effect -- reset validation state when server errors arrive
  }, [serverErrors]);

  return (
    <div className="relative flex flex-col col-span-full gap-2.5 pb-6">
      <label 
        htmlFor={id}
        className="text-sm lg:text-base text-(--gold) font-semibold"
      >
        {label}
        {required && <span className="text-(--brand) ml-1">*</span>}
      </label>
      <textarea
        className={`block resize-y w-full min-h-32 px-4 py-3 text-base bg-white/10 text-white border rounded-lg focus:ring-2 focus:ring-(--brand)/30 focus:outline-none transition-all placeholder:text-white/40 backdrop-blur-sm ${
          allErrors.length > 0
            ? "border-red-400 focus:border-red-400"
            : "border-white/20 focus:border-(--brand)"
        }`}
        id={id}
        name={id}
        placeholder={placeholder}
        required={required ? true : false}
        maxLength={maxLength}
        defaultValue={defaultValue}
        onChange={handleChange}
        onBlur={handleBlur}
        aria-invalid={allErrors.length > 0}
        aria-describedby={allErrors.length > 0 ? allErrors.map((_, i) => `${id}-error-${i}`).join(" ") : undefined}
      />
      <div className="absolute bottom-0 left-0 right-0">
        <FieldError id={id} errors={allErrors} />
      </div>
    </div>
  );
}
